import { api } from './index'

/**
 * The `demo` row set (VRG-836) — the two FORWARD-ONLY conversions a brand can make on rows
 * Vera authored from their public website during outbound.
 *
 * Company scoping is implicit, exactly as in `introDataApi`: `createApiCall` injects
 * `X-Vera-Company`, and the API reads the company from the auth guard, never from a path or
 * body. There is deliberately no `companyId` parameter — adding one would suggest the
 * endpoint can target another tenant, which it cannot.
 *
 * ⚠️ THE TWO CONVERSIONS ARE NOT THE SAME ACTION AND MUST NOT SHARE COPY. Converting a
 * PRODUCT is a claim about truth and is free. Converting a CERTIFICATE records the
 * activation, so it appears on the brand's next invoice. `ConvertDemoRowModal` is where that
 * difference is spelled out for the reader; this file is where it is spelled out for the
 * next developer.
 *
 * ⚠️ THERE IS NO REVERSE, here or on the server. Nothing turns a real row back into a demo
 * one — for a certificate that would be a self-serve way to un-bill an activation.
 *
 * Deliberately NOT a full mirror of the controller: `GET /demo-data` and `DELETE /demo-data`
 * have no client today, and a wrapper nothing calls is a surface that drifts from the server
 * unobserved. Add them when a surface needs them.
 */

/**
 * Why one reason covers absent, another company's, already-real and `intro` alike: the
 * server says so explicitly, so the response cannot be used to probe which ids are real.
 * Never render a bare `not-a-demo-product` as "not found" — it is not one.
 */
export type ConvertDemoProductRefusal = 'not-a-demo-product'

export interface ConvertDemoProductResult {
  productId: string
  /** True only when THIS call cleared the flag. A second call is a no-op, not an error. */
  converted: boolean
  refusal: ConvertDemoProductRefusal | null
  /** The product's title, for the caller's confirmation copy. Null when nothing matched. */
  title: string | null
  /**
   * Certificates already minted off this product that STAY demo and stay free.
   *
   * The conversion is forward-only and does not reach them, so a surface that implies
   * otherwise is lying about what the brand will be billed for. Say the number.
   */
  demoCertificatesRetained: number
  /**
   * The company itself is still `is_example`. Unlike the certificate convert, this does
   * NOT refuse on it — a product conversion bills nothing, so there is nothing to be
   * unbillable.
   */
  companyIsExample: boolean
}

export type ConvertDemoCertificateRefusal =
  /** Absent, another company's, already real, or an `intro` sample — one reason, on purpose. */
  | 'not-a-demo-certificate'
  /** The company is still a demo, so a cleared row would never meter. Take the demo over first. */
  | 'company-is-still-a-demo'
  /** No tag, or the named chip is not in `tags`. Metering needs a tag row. */
  | 'certificate-has-no-tag'
  /** No product. Metering needs one. */
  | 'certificate-has-no-product'

export interface ConvertDemoCertificateResult {
  certificateId: string
  converted: boolean
  refusal: ConvertDemoCertificateRefusal | null
  /**
   * A `certificate_metering` row exists for this certificate now — i.e. the activation is
   * on the next invoice.
   *
   * ⚠️ `converted: true` with `metered: false` is a FREE REAL CERTIFICATE and the one result
   * a caller must surface as a fault rather than as success. Every known cause refuses
   * server-side before the flag is cleared, so if it happens the cause is new.
   */
  metered: boolean
  /** The metering row already existed, so nothing NEW was billed. Legacy rows only. */
  alreadyMetered: boolean
}

export const demoDataApi = {
  /**
   * "This scraped product is accurate; keep it." Free. Certificates already minted off it
   * stay demo — the result's `demoCertificatesRetained` is how many.
   */
  async convertProduct(productId: string): Promise<ConvertDemoProductResult> {
    const response = await api.post(`/demo-data/products/${productId}/convert`)
    return response.data as ConvertDemoProductResult
  },

  /**
   * "This certificate is real; keep it." ⚠️ BILLABLE — clears the flag AND records the
   * activation, so the certificate appears on the next invoice. Metered TODAY, never
   * backdated: the billable event is the brand saying "keep this".
   */
  async convertCertificate(certificateId: string): Promise<ConvertDemoCertificateResult> {
    const response = await api.post(`/demo-data/certificates/${certificateId}/convert`)
    return response.data as ConvertDemoCertificateResult
  },
}
